import type { Maybe } from '@adornis/base/utilTypes.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { html } from 'lit';

export const renderReadableInteger = (value: number) =>
  new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(value);

export const renderUptime = (lastDeploy: Maybe<Date>, endTime?: Maybe<Date>) => {
  if (!lastDeploy) return '–';
  const now = endTime ?? new Date();
  const diff = now.getTime() - lastDeploy.getTime();

  const weeks = Math.floor(diff / (1000 * 60 * 60 * 24 * 7));
  const days = Math.floor((diff % (1000 * 60 * 60 * 24 * 7)) / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (weeks > 0) {
    return `${weeks}w`;
  }

  if (days > 0) {
    return `${days}d`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${minutes}m`;
};

/** The icon names the section, so it stays neutral: colour on this page is reserved for states. */
export const renderDashboardCardHeader = (
  title: string,
  iconClass: string,
  actions?: Renderable,
  subtitle?: Renderable,
) =>
  CardHeader({
    title: html`
      <span class="flex items-center gap-2">
        <span class=${`${iconClass} size-6 shrink-0 text-base-content/70`}></span>
        <span>${title}</span>
      </span>
    `,
    subtitle,
    actions,
  });

export const renderDashboardLoadingCard = () => html`
  <div class="card h-full">
    <div class="card-body min-h-48 items-center justify-center">
      <span class="loading loading-spinner"></span>
    </div>
  </div>
`;

/** Marks a card whose numbers update by themselves, so nobody reloads the page to refresh them. */
export const renderLiveBadge = () => html`
  <span class="flex items-center gap-2 text-sm font-semibold text-base-content/70">
    <span class="inline-grid *:[grid-area:1/1]">
      <span class="status status-success animate-ping"></span>
      <span class="status status-success"></span>
    </span>
    Live
  </span>
`;
